/**
 * Master Layout — Vuex module (namespaced: `masterLayout`).
 *
 * Authoring model for the Company Workbook master layout:
 *   Master Layout -> ordered Stages -> ordered Screens -> ordered Placements.
 * A placement carries: inclusion ('always_on' | 'carrier_derived') which
 * controls questionnaire membership, an optional widget override, and an
 * optional structured conditional (which controls visibility).
 *
 * Backend contract lives under `/admin/master-layout/*`. Master Layout does
 * NOT own a publish lifecycle — its edits are drafts that surface in the
 * existing Draft -> Validate -> Publish workflow (draftPublish module,
 * `/admin/mapping-workspace/draft-publish`). Blocking is decided solely by
 * backend-reported blockers there; a dangling conditional is a client-side
 * WARNING only.
 *
 * Mocks are used ONLY when VUE_APP_MASTER_LAYOUT_MOCK === "true". There is no
 * automatic fallback to the mock when a live request errors.
 */
import axios from "@/plugins/axios";
import {
  isMasterLayoutMockEnabled,
  mockMasterLayoutRequest,
  primeMockCanonicalQuestions,
} from "@/services/masterLayoutMockService";
import { mapLayoutFromApi, mapCanonicalQuestion } from "@/helpers/cwbMappers";

const BASE = "/admin/master-layout";

/**
 * Unified request helper. Returns the axios-style `{ data }` envelope.
 * - Mock flag on  -> isolated mock.
 * - Flag off      -> live axios only (errors propagate; NO mock fallback).
 */
async function mlRequest(method, path, body) {
  if (isMasterLayoutMockEnabled()) {
    return mockMasterLayoutRequest(method, path, body);
  }
  return axios({ method, url: path, data: body, withoutLoading: true });
}

function collectPlacedCanonicalQuestionIds(layout) {
  const ids = [];
  eachLayoutPlacement(layout, (p) => {
    if (p.canonical_question_id != null) ids.push(p.canonical_question_id);
  });
  return Array.from(new Set(ids));
}

function danglingConditionalWarning(placement, parentCanonicalId) {
  return {
    severity: "warning",
    message: `Placement "${
      placement.display_label || placement.title || placement.machine_key
    }" has a conditional on canonical question "${parentCanonicalId}", which is not placed in the layout.`,
  };
}

function eachLayoutPlacement(layout, visit) {
  for (const st of layout.stages) {
    for (const sc of st.screens) {
      for (const p of sc.placements) visit(p);
    }
  }
}

function warningsForPlacement(placement, placedIds) {
  const clauses = placement.conditional?.clauses;
  if (!Array.isArray(clauses)) return [];

  const warnings = [];
  for (const c of clauses) {
    if (c.parentCanonicalId != null && !placedIds.has(c.parentCanonicalId)) {
      warnings.push(danglingConditionalWarning(placement, c.parentCanonicalId));
    }
  }
  return warnings;
}

function collectLayoutWarnings(layout, placedIds) {
  const warnings = [];
  eachLayoutPlacement(layout, (placement) => {
    warnings.push(...warningsForPlacement(placement, placedIds));
  });
  return warnings;
}

const state = {
  layout: null,
  loading: false,
  selectedStageId: null,
  selectedScreenId: null,
  selectedPlacementId: null,
};

const mutations = {
  SET_LAYOUT(state, layout) {
    state.layout = layout;
  },
  SET_LOADING(state, value) {
    state.loading = value;
  },
  SET_SELECTED_STAGE(state, id) {
    state.selectedStageId = id;
  },
  SET_SELECTED_SCREEN(state, id) {
    state.selectedScreenId = id;
  },
  SET_SELECTED_PLACEMENT(state, id) {
    state.selectedPlacementId = id;
  },
};

const getters = {
  layout: (s) => s.layout,
  loading: (s) => s.loading,
  stages: (s) => (s.layout ? s.layout.stages : []),

  selectedStage: (s) => (s.layout ? s.layout.stages.find((st) => st.entity_id === s.selectedStageId) || null : null),

  screensForSelectedStage: (s, g) => (g.selectedStage ? g.selectedStage.screens : []),

  selectedScreen: (s, g) => g.screensForSelectedStage.find((sc) => sc.entity_id === s.selectedScreenId) || null,

  placementsForSelectedScreen: (s, g) => (g.selectedScreen ? g.selectedScreen.placements : []),

  selectedPlacement: (s, g) => g.placementsForSelectedScreen.find((p) => p.entity_id === s.selectedPlacementId) || null,

  selectedStageId: (s) => s.selectedStageId,
  selectedScreenId: (s) => s.selectedScreenId,
  selectedPlacementId: (s) => s.selectedPlacementId,

  // Canonical questions available to place / to use as conditional parents.
  // Sourced from the questionLibrary module and mapped to the aligned vocab.
  canonicalQuestionOptions: (s, g, rootState, rootGetters) => {
    const questions = rootGetters["questionLibrary/questions"] || [];
    return questions.filter((q) => q.status !== "Deprecated").map(mapCanonicalQuestion);
  },

  // Canonical question IDs already placed anywhere in the layout. Conditional
  // parents (parentCanonicalId) are canonical question entity_ids, so dangling
  // checks compare against these — not machine keys.
  placedCanonicalQuestionIds: (s) => {
    if (!s.layout) return [];
    return collectPlacedCanonicalQuestionIds(s.layout);
  },

  /**
   * Client-side WARNINGS only (never blocking). Currently: dangling
   * conditionals — a clause whose parent canonical is not placed anywhere.
   * Publishing is gated exclusively by backend-reported blockers in the
   * Draft/Publish workspace, not by these.
   */
  layoutWarnings: (s, g) => {
    if (!s.layout) return [];
    return collectLayoutWarnings(s.layout, new Set(g.placedCanonicalQuestionIds));
  },
};

const actions = {
  async fetchMasterLayout({ commit, dispatch }, options = {}) {
    if (!options.silent) commit("SET_LOADING", true);
    try {
      const { data } = await mlRequest("GET", BASE);
      if (data.success) {
        commit("SET_LAYOUT", mapLayoutFromApi(data.layout));
        dispatch("ensureSelection");
      }
    } finally {
      if (!options.silent) commit("SET_LOADING", false);
    }
  },

  ensureSelection({ state, commit, getters }) {
    const stages = getters.stages;
    let stage = stages.find((st) => st.entity_id === state.selectedStageId);
    if (!stage) stage = stages[0] || null;
    commit("SET_SELECTED_STAGE", stage ? stage.entity_id : null);

    const screens = stage ? stage.screens : [];
    let screen = screens.find((sc) => sc.entity_id === state.selectedScreenId);
    if (!screen) screen = screens[0] || null;
    commit("SET_SELECTED_SCREEN", screen ? screen.entity_id : null);

    const placements = screen ? screen.placements : [];
    const placement = placements.find((p) => p.entity_id === state.selectedPlacementId);
    if (!placement) commit("SET_SELECTED_PLACEMENT", null);
  },

  selectStage({ commit, dispatch }, stageId) {
    commit("SET_SELECTED_STAGE", stageId);
    commit("SET_SELECTED_SCREEN", null);
    commit("SET_SELECTED_PLACEMENT", null);
    dispatch("ensureSelection");
  },
  selectScreen({ commit, dispatch }, screenId) {
    commit("SET_SELECTED_SCREEN", screenId);
    commit("SET_SELECTED_PLACEMENT", null);
    dispatch("ensureSelection");
  },
  selectPlacement({ commit }, placementId) {
    commit("SET_SELECTED_PLACEMENT", placementId);
  },

  /* ---- stages ---- */
  async createStage({ dispatch }, { title }) {
    const { data } = await mlRequest("POST", `${BASE}/stages`, { title });
    await dispatch("fetchMasterLayout", { silent: true });
    if (data.success && data.stage?.entity_id) dispatch("selectStage", data.stage.entity_id);
    return data;
  },
  async updateStage({ dispatch }, { stageId, title }) {
    const { data } = await mlRequest("PUT", `${BASE}/stages/${stageId}`, { title });
    await dispatch("fetchMasterLayout", { silent: true });
    return data;
  },
  async deleteStage({ dispatch }, stageId) {
    const { data } = await mlRequest("DELETE", `${BASE}/stages/${stageId}`);
    await dispatch("fetchMasterLayout", { silent: true });
    return data;
  },
  async reorderStages({ dispatch }, orderedIds) {
    const { data } = await mlRequest("PUT", `${BASE}/stages/reorder`, { order: orderedIds });
    await dispatch("fetchMasterLayout", { silent: true });
    return data;
  },
  moveStage({ getters, dispatch }, { stageId, direction }) {
    const ids = getters.stages.map((s) => s.entity_id);
    const idx = ids.indexOf(stageId);
    const swap = direction === "up" ? idx - 1 : idx + 1;
    if (idx === -1 || swap < 0 || swap >= ids.length) return Promise.resolve();
    [ids[idx], ids[swap]] = [ids[swap], ids[idx]];
    return dispatch("reorderStages", ids);
  },

  /* ---- screens ---- */
  async createScreen({ dispatch }, { stageId, title }) {
    const { data } = await mlRequest("POST", `${BASE}/stages/${stageId}/screens`, { title });
    await dispatch("fetchMasterLayout", { silent: true });
    if (data.success && data.screen?.entity_id) dispatch("selectScreen", data.screen.entity_id);
    return data;
  },
  async updateScreen({ dispatch }, { screenId, title }) {
    const { data } = await mlRequest("PUT", `${BASE}/screens/${screenId}`, { title });
    await dispatch("fetchMasterLayout", { silent: true });
    return data;
  },
  async deleteScreen({ dispatch }, screenId) {
    const { data } = await mlRequest("DELETE", `${BASE}/screens/${screenId}`);
    await dispatch("fetchMasterLayout", { silent: true });
    return data;
  },
  async moveScreenToStage({ dispatch }, { screenId, stageId }) {
    const { data } = await mlRequest("PUT", `${BASE}/screens/${screenId}/move`, {
      stage_id: stageId,
    });
    await dispatch("fetchMasterLayout", { silent: true });
    return data;
  },
  async reorderScreens({ dispatch }, { stageId, orderedIds }) {
    const { data } = await mlRequest("PUT", `${BASE}/stages/${stageId}/screens/reorder`, {
      order: orderedIds,
    });
    await dispatch("fetchMasterLayout", { silent: true });
    return data;
  },
  moveScreen({ getters, dispatch }, { screenId, direction }) {
    const stage = getters.selectedStage;
    if (!stage) return Promise.resolve();
    const ids = stage.screens.map((sc) => sc.entity_id);
    const idx = ids.indexOf(screenId);
    const swap = direction === "up" ? idx - 1 : idx + 1;
    if (idx === -1 || swap < 0 || swap >= ids.length) return Promise.resolve();
    [ids[idx], ids[swap]] = [ids[swap], ids[idx]];
    return dispatch("reorderScreens", { stageId: stage.entity_id, orderedIds: ids });
  },

  /* ---- placements ---- */
  async createPlacement({ dispatch }, { screenId, canonicalQuestion }) {
    // Prime the dev-only mock registry so it can emulate the BE canonical join
    // for display; this does NOT affect the (clean) outbound payload.
    if (isMasterLayoutMockEnabled()) {
      primeMockCanonicalQuestions([canonicalQuestion]);
    }
    // Send ONLY the placement-owned field. Canonical content (title, options,
    // widget, help text, display label) is joined/nested by the BE on read.
    const { data } = await mlRequest("POST", `${BASE}/screens/${screenId}/placements`, {
      canonical_question_id: canonicalQuestion.entity_id,
    });
    await dispatch("fetchMasterLayout", { silent: true });
    if (data.success && data.placement?.entity_id) {
      dispatch("selectPlacement", data.placement.entity_id);
    }
    return data;
  },
  // patch may contain placement-owned fields only: inclusion, widget_override,
  // conditional (display_label is canonical-owned, not editable on a placement)
  async updatePlacement({ dispatch }, { placementId, patch }) {
    const { data } = await mlRequest("PUT", `${BASE}/placements/${placementId}`, patch);
    await dispatch("fetchMasterLayout", { silent: true });
    return data;
  },
  async deletePlacement({ dispatch }, placementId) {
    const { data } = await mlRequest("DELETE", `${BASE}/placements/${placementId}`);
    await dispatch("fetchMasterLayout", { silent: true });
    return data;
  },
  async movePlacementToScreen({ dispatch }, { placementId, screenId }) {
    const { data } = await mlRequest("PUT", `${BASE}/placements/${placementId}/move`, {
      screen_id: screenId,
    });
    await dispatch("fetchMasterLayout", { silent: true });
    return data;
  },
  async reorderPlacements({ dispatch }, { screenId, orderedIds }) {
    const { data } = await mlRequest("PUT", `${BASE}/screens/${screenId}/placements/reorder`, {
      order: orderedIds,
    });
    await dispatch("fetchMasterLayout", { silent: true });
    return data;
  },
  movePlacement({ getters, dispatch }, { placementId, direction }) {
    const screen = getters.selectedScreen;
    if (!screen) return Promise.resolve();
    const ids = screen.placements.map((p) => p.entity_id);
    const idx = ids.indexOf(placementId);
    const swap = direction === "up" ? idx - 1 : idx + 1;
    if (idx === -1 || swap < 0 || swap >= ids.length) return Promise.resolve();
    [ids[idx], ids[swap]] = [ids[swap], ids[idx]];
    return dispatch("reorderPlacements", { screenId: screen.entity_id, orderedIds: ids });
  },
  setPlacementInclusion({ dispatch }, { placementId, inclusion }) {
    return dispatch("updatePlacement", { placementId, patch: { inclusion } });
  },
  clearPlacementOverride({ dispatch }, placementId) {
    return dispatch("updatePlacement", { placementId, patch: { widget_override: null } });
  },
  clearPlacementConditional({ dispatch }, placementId) {
    return dispatch("updatePlacement", { placementId, patch: { conditional: null } });
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions,
};
